import dayjs from 'dayjs';
import i18n from 'i18next';
import AmicaleApi from '../../../../../../../api/AmicaleApi';

const tq = (key, options) => i18n.t(`moteurReservation.amicalQuote.${key}`, options);

const formatDate = (value) => {
  if (!value) return '-';
  const d = dayjs(value);
  return d.isValid() ? d.format('DD/MM/YYYY') : String(value);
};

const escapeHtml = (str) => {
  if (str == null) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

const buildRoomsHtml = (roomsConfig = [], guestDetails = {}) => {
  const rooms = guestDetails.rooms || [];
  return roomsConfig
    .map((cfg, idx) => {
      const roomGuests = rooms[idx]?.guests || [];
      const guestList = roomGuests
        .map(
          (g) =>
            `<li>${escapeHtml(g.last_name || '')} ${escapeHtml(g.first_name || '')}${
              g.type && g.type !== 'adult' ? ` <em>(${escapeHtml(g.type)})</em>` : ''
            }</li>`
        )
        .join('');

      return `
        <tr>
          <td>${idx + 1}</td>
          <td>${escapeHtml(cfg.room_type || tq('defaultRoom'))}</td>
          <td>${escapeHtml(cfg.arrangement || '-')}</td>
          <td style="text-align:center;">${cfg.adults || 0}</td>
          <td style="text-align:center;">${cfg.children || 0}</td>
          <td>
            ${guestList ? `<ul style="margin:0; padding-left:18px;">${guestList}</ul>` : '-'}
          </td>
        </tr>
      `;
    })
    .join('');
};

const buildAmicalQuoteHtml = ({
  hotel,
  client,
  amicalForm,
  voucherEmail,
  checkin,
  checkout,
  totalPrice,
  currency,
  roomsConfig = [],
  guestDetails = {},
  reservationId,
  bgDataUri = null,
}) => {
  const today = dayjs().format('DD/MM/YYYY HH:mm');
  const titre = reservationId
    ? tq('quoteTitleWithId', { id: reservationId })
    : tq('quoteTitle');

  const bgStyle = bgDataUri
    ? `background-image: url('${bgDataUri}'); background-size: cover; background-position: center; background-repeat: no-repeat;`
    : '';

  const lang = (i18n.language || 'fr').split('-')[0];

  return `<!DOCTYPE html>
<html lang="${escapeHtml(lang)}">
<head>
  <meta charset="UTF-8" />
  <title>${escapeHtml(titre)}</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: 'Segoe UI', Arial, sans-serif; color: #1f2937; padding: 24px; ${bgStyle} min-height: 100vh; }
    h1 { font-size: 22px; margin: 0 0 4px 0; color: #1e3a8a; }
    h2 { font-size: 14px; margin: 18px 0 8px; color: #1e3a8a; border-bottom: 1px solid rgba(229,231,235,0.7); padding-bottom: 4px; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #1e3a8a; padding-bottom: 12px; margin-bottom: 16px; }
    .meta { font-size: 12px; color: #6b7280; }
    .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 24px; font-size: 13px; }
    .info-grid div span:first-child { color: #6b7280; display: inline-block; min-width: 130px; }
    .info-grid div span:last-child { color: #111827; font-weight: 500; }
    table { width: 100%; border-collapse: collapse; font-size: 12px; margin-top: 6px; }
    th, td { border: 1px solid rgba(229,231,235,0.8); padding: 6px 8px; vertical-align: top; background: rgba(255,255,255,0.75); }
    th { background: rgba(243,244,246,0.85); text-align: left; color: #374151; font-weight: 600; }
    .content { background: rgba(255,255,255,0.82); border-radius: 8px; padding: 20px; backdrop-filter: blur(2px); }
    .total { margin-top: 16px; text-align: right; font-size: 15px; }
    .total strong { color: #1e3a8a; font-size: 18px; }
    .footer { margin-top: 28px; font-size: 11px; color: #6b7280; text-align: center; border-top: 1px dashed #e5e7eb; padding-top: 8px; }
    @media print {
      body { padding: 8px; }
      .no-print { display: none !important; }
    }
  </style>
</head>
<body>
<div class="content">
  <div class="header">
    <div>
      <h1>${escapeHtml(titre)}</h1>
      <div class="meta">${escapeHtml(tq('editedOn', { date: today }))}</div>
    </div>
    <div style="text-align:right;">
      <div style="font-weight:600; color:#1e3a8a;">${escapeHtml(client?.name || tq('defaultAmicale'))}</div>
      ${voucherEmail ? `<div style="font-size:12px; color:#6b7280;">${escapeHtml(voucherEmail)}</div>` : ''}
    </div>
  </div>

  <h2>${escapeHtml(tq('hotel'))}</h2>
  <div class="info-grid">
    <div><span>${escapeHtml(tq('name'))}</span> <span>${escapeHtml(hotel?.name || '-')}</span></div>
    <div><span>${escapeHtml(tq('location'))}</span> <span>${escapeHtml(
      [hotel?.ville_name, hotel?.pays_name].filter(Boolean).join(', ') || '-'
    )}</span></div>
    <div><span>${escapeHtml(tq('checkIn'))}</span> <span>${escapeHtml(formatDate(checkin))}</span></div>
    <div><span>${escapeHtml(tq('checkOut'))}</span> <span>${escapeHtml(formatDate(checkout))}</span></div>
  </div>

  <h2>${escapeHtml(tq('member'))}</h2>
  <div class="info-grid">
    <div><span>${escapeHtml(tq('civility'))}</span> <span>${escapeHtml(amicalForm?.civilite_amical || '-')}</span></div>
    <div><span>${escapeHtml(tq('registration'))}</span> <span>${escapeHtml(amicalForm?.matricule_fiscal_amical || '-')}</span></div>
    <div><span>${escapeHtml(tq('phone'))}</span> <span>${escapeHtml(amicalForm?.telephone_amical || '-')}</span></div>
    <div><span>${escapeHtml(tq('voucherEmail'))}</span> <span>${escapeHtml(voucherEmail || '-')}</span></div>
  </div>

  <h2>${escapeHtml(tq('roomsAndOccupants'))}</h2>
  <table>
    <thead>
      <tr>
        <th>#</th>
        <th>${escapeHtml(tq('tableRoom'))}</th>
        <th>${escapeHtml(tq('tableArrangement'))}</th>
        <th>${escapeHtml(tq('tableAdults'))}</th>
        <th>${escapeHtml(tq('tableChildren'))}</th>
        <th>${escapeHtml(tq('tableOccupants'))}</th>
      </tr>
    </thead>
    <tbody>
      ${buildRoomsHtml(roomsConfig, guestDetails)}
    </tbody>
  </table>

  <div class="total">
    ${escapeHtml(tq('estimatedTotal'))} <strong>${Number(totalPrice || 0).toFixed(2)} ${escapeHtml(currency)}</strong>
  </div>

</div>
</body>
</html>`;
};

export const printAmicalQuote = async (params) => {
  const bgDataUri = await AmicaleApi.getVoucherBgDataUri().catch(() => null);
  const html = buildAmicalQuoteHtml({ ...params, bgDataUri });
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  document.body.appendChild(iframe);

  iframe.onload = () => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (err) {
      console.error('[AmicalQuote] print failed:', err);
    }
    setTimeout(() => {
      try {
        if (iframe.parentNode) document.body.removeChild(iframe);
      } catch (_) {
        /* noop */
      }
    }, 1500);
  };

  const doc = iframe.contentDocument || iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
};

export const downloadAmicalQuote = (params) => {
  const html = buildAmicalQuoteHtml(params);
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const id = params?.reservationId ? `-${params.reservationId}` : '';
  a.href = url;
  a.download = `${tq('downloadFilePrefix')}${id}.html`;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    if (a.parentNode) document.body.removeChild(a);
  }, 100);
};

export default printAmicalQuote;
